import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { getFarmOverviewPath } from '../utils/farmRoutes';
import './HomePage.css';

export default function HomePage() {
  const { lang } = useLang();
  const { token, user } = useAuth();
  const { effectiveFarmId } = useFarm();
  const farmOverviewPath = getFarmOverviewPath(effectiveFarmId || user?.farmId) || '/dashboard';
  const primaryCtaTo = token ? farmOverviewPath : '/login';

  useEffect(() => {
    document.body.classList.add('sg20-landing-active');
    return () => {
      document.body.classList.remove('sg20-landing-active');
    };
  }, []);

  return (
    <main className="sg20-page" dir={lang === 'he' ? 'rtl' : 'ltr'}>
      <LandingMarkup primaryCtaTo={primaryCtaTo} />
    </main>
  );
}

function LandingMarkup({ primaryCtaTo }) {
  const { t } = useLang();
  const tr = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };
  const trArray = (key, fallback) => {
    const translated = t(key);
    return Array.isArray(translated) ? translated : fallback;
  };

  const proBullets = trArray('home.public.pricing.plans.pro.bullets', []);
  const enterpriseBullets = trArray('home.public.pricing.plans.enterprise.bullets', []);
  const strainsBullets = Array.isArray(t('home.landing.modules.strainsBullets'))
    ? t('home.landing.modules.strainsBullets')
    : [];
  const roomsBullets = Array.isArray(t('home.landing.modules.roomsBullets'))
    ? t('home.landing.modules.roomsBullets')
    : [];
  const analyticsBullets = Array.isArray(t('home.landing.modules.analyticsBullets'))
    ? t('home.landing.modules.analyticsBullets')
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f3329] via-[#122b22] to-[#0e2820] relative overflow-hidden">
      {/* Atmospheric Background with Enhanced Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-700/20 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-emerald-800/15 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-transparent via-emerald-950/30 to-transparent"></div>

      {/* Key Section Glows */}
      <div className="absolute top-0 right-1/4 w-[1200px] h-[1200px] bg-emerald-500/15 rounded-full blur-[150px] animate-pulse"></div>
      <div className="absolute top-1/3 right-0 w-[900px] h-[900px] bg-emerald-400/12 rounded-full blur-[140px]"></div>
      <div className="absolute top-2/3 left-0 w-[800px] h-[800px] bg-teal-500/12 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 left-1/3 w-[1000px] h-[1000px] bg-emerald-600/10 rounded-full blur-[160px]"></div>

      {/* Vertical Section Separators */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-900/10 to-transparent"></div>
      <div className="absolute inset-0" style={{background: 'repeating-linear-gradient(to bottom, transparent 0px, transparent 800px, rgba(16, 185, 129, 0.03) 800px, rgba(16, 185, 129, 0.03) 801px)'}}></div>

      {/* Hero Section */}
      <main className="relative z-10 sg20-landing-shell py-8 sm:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8">
          {/* Left Column - Hero Text */}
          <div className="lg:col-span-5 flex flex-col justify-center space-y-6 lg:space-y-8">
            <div className="space-y-4 lg:space-y-5">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight drop-shadow-2xl">
                {tr('home.hero.title', 'Полный контроль цикла выращивания')}
              </h1>
              <p className="text-lg sm:text-xl text-emerald-100/60 leading-relaxed">
                {tr('home.hero.subtitle', 'Сорта, климат, комнаты, контроллеры, аналитика и дневник — в одной защищённой системе.')}
              </p>
            </div>
            <div>
              <Link to={primaryCtaTo} className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold text-lg transition-all hover:shadow-2xl hover:shadow-emerald-500/50 hover:scale-[1.02] shadow-xl shadow-emerald-600/30 inline-flex items-center justify-center no-underline">
                {tr('home.hero.openDashboard', 'Открыть дашборд')}
              </Link>
            </div>
          </div>

          {/* Right Column - Dashboard Preview */}
          <div className="lg:col-span-7 space-y-4 relative">
            {/* Glow effect behind cards */}
            <div className="absolute inset-0 bg-emerald-500/5 blur-3xl rounded-full"></div>

            {/* Top Row - Active Strain Card */}
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-7 border border-emerald-500/30 shadow-2xl shadow-emerald-900/50 hover:border-emerald-400/40 transition-all group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-emerald-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-start justify-between">
                <div className="space-y-3">
                  <div className="text-sm text-emerald-400/80 font-medium">{t('home.public.preview.activeStrain')}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">{t('home.landing.demo.strainName')}</div>
                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-emerald-400/70">{t('home.landing.demo.presetLabel')}</span>
                      <span className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-400/40 shadow-lg shadow-emerald-500/20">
                        {t('home.public.preview.flowering')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-emerald-400/70">{t('home.landing.demo.cycleDayLabel')}</span>
                      <span className="text-white text-sm font-bold">42</span>
                    </div>
                  </div>
                </div>
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/30 to-emerald-500/30 border border-purple-400/40 shadow-2xl shadow-purple-500/30 backdrop-blur-sm"></div>
              </div>
            </div>

            {/* Middle Row - Climate Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-5 border border-emerald-500/30 shadow-xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all group overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/15 transition-all"></div>
                <div className="relative space-y-3">
                  <div className="text-xs text-emerald-400/80 font-semibold uppercase tracking-wide">{t('home.public.capabilities.temperature')}</div>
                  <div className="text-3xl sm:text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">24.5°C</div>
                  <div className="flex items-center gap-1">
                    <div className="w-full h-2 bg-emerald-950/70 rounded-full overflow-hidden shadow-inner">
                      <div className="h-full w-[82%] bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full shadow-lg shadow-emerald-500/60"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-emerald-400/60">{t('home.landing.demo.tempOptimal')}</div>
                    <div className="text-xs text-emerald-300/80 font-semibold">{t('home.landing.demo.tempDelta')}</div>
                  </div>
                </div>
              </div>

              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-5 border border-emerald-500/30 shadow-xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all group overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/15 transition-all"></div>
                <div className="relative space-y-3">
                  <div className="text-xs text-emerald-400/80 font-semibold uppercase tracking-wide">{t('home.public.capabilities.humidity')}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">62%</div>
                  <div className="flex items-center gap-1">
                    <div className="w-full h-2 bg-emerald-950/70 rounded-full overflow-hidden shadow-inner">
                      <div className="h-full w-[62%] bg-gradient-to-r from-cyan-500 to-blue-400 rounded-full shadow-lg shadow-cyan-500/60"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-emerald-400/60">{t('home.landing.demo.humNormal')}</div>
                    <div className="text-xs text-emerald-300/80 font-semibold">{t('home.landing.demo.humDelta')}</div>
                  </div>
                </div>
              </div>

              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-5 border border-emerald-500/30 shadow-xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all group overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/15 transition-all"></div>
                <div className="relative space-y-3">
                  <div className="text-xs text-emerald-400/80 font-semibold uppercase tracking-wide">{t('home.public.capabilities.co2')}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">1200</div>
                  <div className="flex items-center gap-1">
                    <div className="w-full h-2 bg-emerald-950/70 rounded-full overflow-hidden shadow-inner">
                      <div className="h-full w-[75%] bg-gradient-to-r from-amber-500 to-orange-400 rounded-full shadow-lg shadow-amber-500/60"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-emerald-400/60">{t('home.landing.demo.co2Unit')}</div>
                    <div className="text-xs text-emerald-300/80 font-semibold">{t('deviceSettingsModal.modeAuto')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row - Room & Controller Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
                <div className="relative space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-emerald-400/80 font-semibold">{t('home.landing.demo.roomTitle')}</div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/60 animate-pulse"></div>
                      <span className="text-xs text-emerald-300/80 font-medium">{t('home.landing.demo.active')}</span>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-100/50">{t('home.landing.demo.plants')}</span>
                      <span className="text-white font-semibold">{t('home.landing.demo.plantsCount')}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-100/50">{t('home.landing.demo.light')}</span>
                      <span className="text-emerald-400 font-semibold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/60"></span>
                        {t('home.landing.demo.lightOn')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-100/50">{t('home.landing.demo.ventilation')}</span>
                      <span className="text-emerald-400 font-semibold">{t('home.landing.demo.ventilationValue')}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-emerald-500/30">
                      <span className="text-emerald-100/50">{t('home.landing.demo.irrigation')}</span>
                      <span className="text-cyan-400 font-semibold">{t('home.landing.demo.irrigationIn')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
                <div className="relative space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-emerald-400/80 font-semibold">{t('home.landing.demo.controllerTitle')}</div>
                    <div className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-400/30 shadow-sm shadow-emerald-500/20">{t('deviceCard.connectivityOnline')}</div>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-100/50">{t('home.landing.demo.sensors')}</span>
                      <span className="text-white font-semibold">8/8</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-100/50">{t('home.landing.panel.outlets')}</span>
                      <span className="text-emerald-400 font-semibold">{t('home.landing.demo.outletsLine')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Why Shefa Green Section */}
      <div className="absolute left-1/2 -translate-x-1/2 w-landing-glow h-[600px] bg-emerald-600/12 rounded-full blur-[120px] pointer-events-none" style={{top: '800px'}}></div>
      <section id="modules" className="relative z-10 sg20-landing-shell py-16 sm:py-20 lg:py-24">
        <div className="text-center space-y-4 mb-12 sm:mb-14 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{tr('home.public.valueTitle', 'Почему Shefa Green')}</h2>
          <p className="text-lg sm:text-xl text-emerald-100/60 max-w-3xl mx-auto leading-relaxed">
            {tr('home.public.valueSubtitle', 'Система собирает сорта, комнаты, устройства, климат, воду, дневник и задачи в один понятный рабочий слой для гроувера.')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Card 1 - Сорта в центре */}
          <div className="relative group">
            <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-3xl group-hover:bg-emerald-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-8 border border-emerald-500/30 shadow-2xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 blur-3xl rounded-full"></div>
              <div className="relative space-y-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/30 to-emerald-500/30 border border-purple-400/40 shadow-xl shadow-purple-500/30 flex items-center justify-center">
                  <svg className="w-8 h-8 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-white">{tr('home.public.modules.strains.title', 'Сорта в центре')}</h3>
                  <p className="text-emerald-100/60 leading-relaxed">
                    {tr('home.public.modules.strains.description', 'Каждый сорт связан с черенками, комнатами, стадиями роста, заметками и историей выращивания.')}
                  </p>
                </div>
                <div className="pt-4 space-y-2">
                  {strainsBullets.map((line) => (
                    <div key={line} className="flex items-center gap-2 text-sm text-emerald-300/80">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 - Комнаты под контролем */}
          <div className="relative group">
            <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-3xl group-hover:bg-emerald-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-8 border border-emerald-500/30 shadow-2xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 blur-3xl rounded-full"></div>
              <div className="relative space-y-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 border border-cyan-400/40 shadow-xl shadow-cyan-500/30 flex items-center justify-center">
                  <svg className="w-8 h-8 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-white">{tr('home.public.modules.climateAutomation.title', 'Комнаты под контролем')}</h3>
                  <p className="text-emerald-100/60 leading-relaxed">
                    {tr('home.public.modules.climateAutomation.description', 'Гроувер видит состояние каждой комнаты: климат, воду, оборудование, розетки и текущие отклонения.')}
                  </p>
                </div>
                <div className="pt-4 space-y-2">
                  {roomsBullets.map((line) => (
                    <div key={line} className="flex items-center gap-2 text-sm text-emerald-300/80">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 - Решения на основе данных */}
          <div className="relative group">
            <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-3xl group-hover:bg-emerald-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-8 border border-emerald-500/30 shadow-2xl shadow-emerald-900/40 hover:border-emerald-400/40 transition-all h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 blur-3xl rounded-full"></div>
              <div className="relative space-y-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/30 to-emerald-500/30 border border-amber-400/40 shadow-xl shadow-amber-500/30 flex items-center justify-center">
                  <svg className="w-8 h-8 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-white">{tr('home.public.modules.analytics.title', 'Решения на основе данных')}</h3>
                  <p className="text-emerald-100/60 leading-relaxed">
                    {tr('home.public.modules.analytics.description', 'Дневник, задачи и аналитика помогают понимать, что происходило в цикле и что нужно проверить сейчас.')}
                  </p>
                </div>
                <div className="pt-4 space-y-2">
                  {analyticsBullets.map((line) => (
                    <div key={line} className="flex items-center gap-2 text-sm text-emerald-300/80">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How System Works Section */}
      <div className="absolute left-1/2 -translate-x-1/2 w-landing-glow h-[500px] bg-emerald-500/14 rounded-full blur-[100px] pointer-events-none" style={{top: '1600px'}}></div>
      <section className="relative z-10 sg20-landing-shell py-16 sm:py-20 lg:py-24">
        <div className="text-center space-y-4 mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{t('home.landing.flow.title')}</h2>
          <p className="text-lg sm:text-xl text-emerald-100/60 max-w-3xl mx-auto leading-relaxed">
            {t('home.landing.flow.subtitle')}
          </p>
        </div>

        <div className="relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-1/3 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent"></div>
          <div className="hidden lg:block absolute top-1/3 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent blur-sm"></div>

          {/* Flow Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 lg:gap-6 relative">
            {/* Step 1 - Сорт */}
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-2xl group-hover:bg-emerald-500/10 transition-all"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl hover:border-emerald-400/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 blur-2xl rounded-full"></div>
                <div className="relative space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/30 to-emerald-500/30 border border-purple-400/40 shadow-lg flex items-center justify-center mx-auto">
                    <div className="w-6 h-6 rounded-full bg-purple-400/80 shadow-lg shadow-purple-500/50"></div>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="text-xs text-emerald-400/60 font-medium uppercase tracking-wider">{t('home.landing.flow.stepLabel', 1)}</div>
                    <h3 className="text-xl font-bold text-white">{t('home.landing.flow.step1Title')}</h3>
                    <p className="text-sm text-emerald-100/60 leading-relaxed">
                      {t('home.landing.flow.step1Desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 - Черенки */}
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-2xl group-hover:bg-emerald-500/10 transition-all"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl hover:border-emerald-400/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 blur-2xl rounded-full"></div>
                <div className="relative space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-emerald-400/40 shadow-lg flex items-center justify-center mx-auto">
                    <svg className="w-5 h-5 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="text-xs text-emerald-400/60 font-medium uppercase tracking-wider">{t('home.landing.flow.stepLabel', 2)}</div>
                    <h3 className="text-xl font-bold text-white">{t('home.landing.flow.step2Title')}</h3>
                    <p className="text-sm text-emerald-100/60 leading-relaxed">
                      {t('home.landing.flow.step2Desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 - Комната */}
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-2xl group-hover:bg-emerald-500/10 transition-all"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl hover:border-emerald-400/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 blur-2xl rounded-full"></div>
                <div className="relative space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 border border-cyan-400/40 shadow-lg flex items-center justify-center mx-auto">
                    <svg className="w-5 h-5 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="text-xs text-emerald-400/60 font-medium uppercase tracking-wider">{t('home.landing.flow.stepLabel', 3)}</div>
                    <h3 className="text-xl font-bold text-white">{t('home.landing.flow.step3Title')}</h3>
                    <p className="text-sm text-emerald-100/60 leading-relaxed">
                      {t('home.landing.flow.step3Desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 - Контроллеры */}
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-2xl group-hover:bg-emerald-500/10 transition-all"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl hover:border-emerald-400/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 blur-2xl rounded-full"></div>
                <div className="relative space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 border border-blue-400/40 shadow-lg flex items-center justify-center mx-auto">
                    <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="text-xs text-emerald-400/60 font-medium uppercase tracking-wider">{t('home.landing.flow.stepLabel', 4)}</div>
                    <h3 className="text-xl font-bold text-white">{t('home.landing.flow.step4Title')}</h3>
                    <p className="text-sm text-emerald-100/60 leading-relaxed">
                      {t('home.landing.flow.step4Desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 - Дневник и аналитика */}
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500/5 blur-2xl rounded-2xl group-hover:bg-emerald-500/10 transition-all"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-2xl p-6 border border-emerald-500/30 shadow-xl hover:border-emerald-400/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 blur-2xl rounded-full"></div>
                <div className="relative space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/30 to-emerald-500/30 border border-amber-400/40 shadow-lg flex items-center justify-center mx-auto">
                    <svg className="w-5 h-5 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="text-xs text-emerald-400/60 font-medium uppercase tracking-wider">{t('home.landing.flow.stepLabel', 5)}</div>
                    <h3 className="text-xl font-bold text-white">{t('home.landing.flow.step5Title')}</h3>
                    <p className="text-sm text-emerald-100/60 leading-relaxed">
                      {t('home.landing.flow.step5Desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ShefaGreen Controllers Section */}
      <div className="absolute left-1/2 -translate-x-1/2 w-landing-glow h-[550px] bg-teal-500/14 rounded-full blur-[110px] pointer-events-none" style={{top: '2400px'}}></div>
      <section id="controllers" className="relative z-10 sg20-landing-shell py-16 sm:py-20 lg:py-24">
        <div className="text-center space-y-4 mb-12 sm:mb-14 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{tr('home.public.controllerPill', 'ShefaGreen Controllers')}</h2>
          <p className="text-lg sm:text-xl text-emerald-100/60 max-w-3xl mx-auto leading-relaxed">
            {tr('home.public.controllersBody', 'Контроллеры Shefa Green не только собирают данные, но и помогают управлять климатом, водой и розетками в комнате.')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Side - Text Block */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-4">
              <h3 className="text-3xl font-bold text-white">{tr('home.public.controllersTitle', 'Полный контроль микроклимата')}</h3>
              <p className="text-lg text-emerald-100/60 leading-relaxed">
                {t('home.landing.controllers.para1')}
              </p>
              <p className="text-lg text-emerald-100/60 leading-relaxed">
                {t('home.landing.controllers.para2')}
              </p>
            </div>

            <div className="pt-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('home.landing.controllers.feat1Title')}</div>
                  <div className="text-emerald-100/60 text-sm">{t('home.landing.controllers.feat1Body')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('home.landing.controllers.feat2Title')}</div>
                  <div className="text-emerald-100/60 text-sm">{t('home.landing.controllers.feat2Body')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('home.landing.controllers.feat3Title')}</div>
                  <div className="text-emerald-100/60 text-sm">{t('home.landing.controllers.feat3Body')}</div>
                </div>
              </div>
            </div>

            <div className="pt-6 px-6 py-4 rounded-2xl bg-emerald-950/50 border border-emerald-500/30">
              <p className="text-sm text-emerald-100/70 leading-relaxed">
                {t('home.landing.controllers.summary')}
              </p>
            </div>
          </div>

          {/* Right Side - Controller Panel Card */}
          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/5 blur-3xl rounded-3xl"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-8 border border-emerald-500/30 shadow-2xl">
                <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/8 blur-3xl rounded-full"></div>

                <div className="relative space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-emerald-500/30">
                    <div className="space-y-1">
                      <div className="text-sm text-emerald-400/70 font-medium">{t('home.landing.panel.controllerLabel')}</div>
                      <div className="text-2xl font-bold text-white">{t('home.landing.panel.deviceName')}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/60 animate-pulse"></div>
                      <span className="text-emerald-300 text-sm font-semibold">{t('deviceCard.connectivityOnline')}</span>
                    </div>
                  </div>

                  {/* Climate Sensors */}
                  <div>
                    <div className="text-xs text-emerald-400/70 font-medium uppercase tracking-wider mb-3">{t('home.landing.panel.climate')}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-emerald-500/30 hover:border-emerald-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full blur-xl group-hover:bg-emerald-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-emerald-400/70 mb-1">{t('home.public.capabilities.temperature')}</div>
                          <div className="text-lg font-bold text-white">24.5°C</div>
                        </div>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-cyan-500/30 hover:border-cyan-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-full blur-xl group-hover:bg-cyan-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-cyan-400/70 mb-1">{t('home.public.capabilities.humidity')}</div>
                          <div className="text-lg font-bold text-white">62%</div>
                        </div>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-amber-500/30 hover:border-amber-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/10 rounded-full blur-xl group-hover:bg-amber-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-amber-400/70 mb-1">{t('home.public.capabilities.co2')}</div>
                          <div className="text-lg font-bold text-white">1200</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Equipment Control */}
                  <div>
                    <div className="text-xs text-emerald-400/70 font-medium uppercase tracking-wider mb-3">{t('home.landing.panel.equipment')}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-emerald-400/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full blur-xl group-hover:bg-emerald-500/15 transition-all"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.demo.ventilation')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-400/30">{t('home.landing.panel.on')}</span>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-emerald-500/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full blur-xl"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.panel.cooling')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-emerald-950/50 text-emerald-400/50 text-xs font-semibold border border-emerald-500/30">{t('home.landing.panel.off')}</span>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-emerald-500/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full blur-xl"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.panel.dehumidifying')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-emerald-950/50 text-emerald-400/50 text-xs font-semibold border border-emerald-500/30">{t('home.landing.panel.off')}</span>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-cyan-400/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-full blur-xl group-hover:bg-cyan-500/15 transition-all"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.demo.irrigation')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-cyan-500/20 text-cyan-300 text-xs font-semibold border border-cyan-400/30">{t('home.landing.panel.on')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Water Sensors */}
                  <div>
                    <div className="text-xs text-emerald-400/70 font-medium uppercase tracking-wider mb-3">{t('home.landing.panel.water')}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-cyan-500/30 hover:border-cyan-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-12 h-12 bg-cyan-500/10 rounded-full blur-lg group-hover:bg-cyan-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-cyan-400/70 mb-1">{t('home.landing.panel.flow')}</div>
                          <div className="text-base font-bold text-white">2.4<span className="text-xs text-cyan-400/60 ml-1">{t('home.landing.panel.flowUnit')}</span></div>
                        </div>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-amber-500/30 hover:border-amber-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-12 h-12 bg-amber-500/10 rounded-full blur-lg group-hover:bg-amber-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-amber-400/70 mb-1">EC</div>
                          <div className="text-base font-bold text-white">1.8</div>
                        </div>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-blue-500/30 hover:border-blue-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-12 h-12 bg-blue-500/10 rounded-full blur-lg group-hover:bg-blue-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-blue-400/70 mb-1">pH</div>
                          <div className="text-base font-bold text-white">6.2</div>
                        </div>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-purple-500/30 hover:border-purple-400/40 transition-all group overflow-hidden">
                        <div className="absolute top-0 right-0 w-12 h-12 bg-purple-500/10 rounded-full blur-lg group-hover:bg-purple-500/15 transition-all"></div>
                        <div className="relative">
                          <div className="text-xs text-purple-400/70 mb-1">ORP</div>
                          <div className="text-base font-bold text-white">320</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Outlets */}
                  <div>
                    <div className="text-xs text-emerald-400/70 font-medium uppercase tracking-wider mb-3">{t('home.landing.panel.outlets')}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-amber-400/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/10 rounded-full blur-xl group-hover:bg-amber-500/15 transition-all"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.panel.mainLight')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-amber-500/20 text-amber-300 text-xs font-semibold border border-amber-400/30">{t('home.landing.panel.on')}</span>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-emerald-500/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full blur-xl"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.panel.airConditioner')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-emerald-950/50 text-emerald-400/50 text-xs font-semibold border border-emerald-500/30">{t('home.landing.panel.off')}</span>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-cyan-400/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-full blur-xl group-hover:bg-cyan-500/15 transition-all"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.panel.irrigationPump')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-cyan-500/20 text-cyan-300 text-xs font-semibold border border-cyan-400/30">{t('home.landing.panel.on')}</span>
                      </div>
                      <div className="relative px-4 py-3 rounded-xl bg-emerald-950/70 border border-emerald-500/30 flex items-center justify-between overflow-hidden group">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full blur-xl"></div>
                        <span className="relative text-sm text-emerald-100/70">{t('home.landing.panel.dehumidifier')}</span>
                        <span className="relative px-2 py-1 rounded-md bg-emerald-950/50 text-emerald-400/50 text-xs font-semibold border border-emerald-500/30">{t('home.landing.panel.off')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans Section */}
      <div className="absolute left-1/2 -translate-x-1/2 w-landing-glow h-[600px] bg-emerald-600/16 rounded-full blur-[130px] pointer-events-none" style={{top: '3400px'}}></div>
      <section id="pricing" className="relative z-10 sg20-landing-shell py-16 sm:py-20 lg:py-24">
        <div className="text-center space-y-4 mb-12 sm:mb-14 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{tr('home.public.pricing.eyebrow', 'Тарифы')}</h2>
          <p className="text-lg sm:text-xl text-emerald-100/60 max-w-3xl mx-auto leading-relaxed">
            {tr('home.public.pricing.subtitle', 'Все планы включают основные модули Shefa Green. Разница — в глубине хранения климат-истории и доступности расширенного VPD-анализа.')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Plan 1 - Старт */}
          <div className="relative group">
            <div className="absolute inset-0 bg-slate-500/5 blur-xl rounded-3xl group-hover:bg-slate-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-6 border border-slate-500/30 shadow-xl hover:border-slate-400/40 transition-all overflow-hidden flex flex-col min-h-[280px]">
              <div className="absolute top-0 right-0 w-24 h-24 bg-slate-500/10 rounded-full blur-2xl"></div>
              <div className="relative flex-1 flex flex-col">
                <div className="text-center space-y-2 mb-6">
                  <h3 className="text-2xl font-bold text-white">{t('planTier.included')}</h3>
                  <p className="text-xs text-slate-300/60">{tr('home.public.pricing.plans.included.tagline', 'По умолчанию')}</p>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-4 pb-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">1</div>
                    <div className="text-xs text-slate-400/70">{t('home.landing.pricing.historyCaption')}</div>
                  </div>
                  <div className="text-center pt-3 border-t border-slate-500/20">
                    <div className="text-xs text-slate-400/60 mb-1">{t('home.landing.pricing.vpd')}</div>
                    <div className="text-sm text-slate-400/50">{t('home.landing.no')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Plan 2 - Базовый */}
          <div className="relative group">
            <div className="absolute inset-0 bg-cyan-500/5 blur-xl rounded-3xl group-hover:bg-cyan-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-6 border border-cyan-500/30 shadow-xl hover:border-cyan-400/40 transition-all overflow-hidden flex flex-col min-h-[280px]">
              <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl"></div>
              <div className="relative flex-1 flex flex-col">
                <div className="text-center space-y-2 mb-6">
                  <h3 className="text-2xl font-bold text-white">{t('planTier.basic')}</h3>
                  <p className="text-xs text-cyan-300/60">{tr('home.public.pricing.plans.basic.tagline', 'Короткий обзор')}</p>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-4 pb-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">3</div>
                    <div className="text-xs text-cyan-400/70">{t('home.landing.pricing.historyCaption')}</div>
                  </div>
                  <div className="text-center pt-3 border-t border-cyan-500/20">
                    <div className="text-xs text-cyan-400/60 mb-1">{t('home.landing.pricing.vpd')}</div>
                    <div className="text-sm text-cyan-400/50">{t('home.landing.no')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Plan 3 - Стандарт */}
          <div className="relative group">
            <div className="absolute inset-0 bg-blue-500/5 blur-xl rounded-3xl group-hover:bg-blue-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-6 border border-blue-500/30 shadow-xl hover:border-blue-400/40 transition-all overflow-hidden flex flex-col min-h-[280px]">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
              <div className="relative flex-1 flex flex-col">
                <div className="text-center space-y-2 mb-6">
                  <h3 className="text-2xl font-bold text-white">{t('planTier.standard')}</h3>
                  <p className="text-xs text-blue-300/60">{tr('home.public.pricing.plans.standard.tagline', 'Недельное окно')}</p>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-4 pb-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">7</div>
                    <div className="text-xs text-blue-400/70">{t('home.landing.pricing.historyCaption')}</div>
                  </div>
                  <div className="text-center pt-3 border-t border-blue-500/20">
                    <div className="text-xs text-blue-400/60 mb-1">{t('home.landing.pricing.vpd')}</div>
                    <div className="text-sm text-blue-400/50">{t('home.landing.no')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Plan 4 - Pro (Highlighted) */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/30 to-emerald-400/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-900/70 to-emerald-800/50 backdrop-blur-2xl rounded-3xl p-6 border-2 border-emerald-400/50 shadow-2xl shadow-emerald-500/40 overflow-hidden flex flex-col min-h-[280px]">
              <div className="absolute top-3 right-3">
                <div className="px-2.5 py-1 rounded-full bg-emerald-500/30 border border-emerald-400/50 text-[10px] font-bold text-emerald-200 shadow-lg">
                  {t('home.landing.recommended')}
                </div>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl"></div>

              <div className="relative flex-1 flex flex-col">
                <div className="text-center space-y-2 mb-6">
                  <h3 className="text-2xl font-bold text-white">{t('planTier.pro')}</h3>
                  <p className="text-xs text-emerald-300/70">{tr('home.public.pricing.plans.pro.tagline', 'Месяц и VPD')}</p>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-4 pb-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">30</div>
                    <div className="text-xs text-emerald-400/70">{t('home.landing.pricing.historyCaption')}</div>
                  </div>
                  <div className="text-center pt-3 border-t border-emerald-400/30">
                    <div className="text-xs text-emerald-400/60 mb-1">{t('home.landing.pricing.vpd')}</div>
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/60"></div>
                      <div className="text-sm font-semibold text-emerald-300">{t('home.landing.yes')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Plan 5 - Enterprise */}
          <div className="relative group">
            <div className="absolute inset-0 bg-purple-500/5 blur-xl rounded-3xl group-hover:bg-purple-500/10 transition-all"></div>
            <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-6 border border-purple-500/30 shadow-xl hover:border-purple-400/40 transition-all overflow-hidden flex flex-col min-h-[280px]">
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl"></div>
              <div className="relative flex-1 flex flex-col">
                <div className="text-center space-y-2 mb-6">
                  <h3 className="text-2xl font-bold text-white">{t('planTier.enterprise')}</h3>
                  <p className="text-xs text-purple-300/60">{tr('home.public.pricing.plans.enterprise.tagline', 'Долгий архив')}</p>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-4 pb-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">365</div>
                    <div className="text-xs text-purple-400/70">{t('home.landing.pricing.historyCaption')}</div>
                  </div>
                  <div className="text-center pt-3 border-t border-purple-500/20">
                    <div className="text-xs text-purple-400/60 mb-1">{t('home.landing.pricing.vpd')}</div>
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400 shadow-sm shadow-purple-400/60"></div>
                      <div className="text-sm font-semibold text-purple-300">{t('home.landing.yes')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mt-12 text-center">
          <div className="inline-block px-8 py-4 rounded-2xl bg-emerald-950/50 border border-emerald-500/30 backdrop-blur-sm">
            <p className="text-sm text-emerald-100/70 leading-relaxed max-w-2xl">
              {tr('home.public.pricing.footer', 'Тариф назначает администратор Shefa Green. Чтобы сменить план фермы, свяжитесь с поддержкой.')}
            </p>
          </div>
        </div>
      </section>

      {/* Future AI Assistant Section */}
      <div className="absolute left-1/2 -translate-x-1/2 w-landing-glow h-[600px] bg-emerald-500/12 rounded-full blur-[140px] pointer-events-none" style={{top: '4400px'}}></div>
      <section className="relative z-10 sg20-landing-shell py-16 sm:py-20 lg:py-24">
        <div className="text-center space-y-4 mb-12 sm:mb-14 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{t('home.landing.ai.sectionTitle')}</h2>
          <p className="text-lg sm:text-xl text-emerald-100/60 max-w-3xl mx-auto leading-relaxed">
            {tr('home.public.modules.aiRoadmap.description', 'Shefa Green готовит данные так, чтобы в будущем интеллектуальный слой мог понимать историю выращивания, находить паттерны и помогать гроуверу принимать более точные решения.')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Side - Text Content */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-4">
              <h3 className="text-2xl lg:text-3xl font-bold text-white">{t('home.landing.ai.heading')}</h3>
              <p className="text-base lg:text-lg text-emerald-100/70 leading-relaxed">
                {t('home.landing.ai.body1')}
              </p>
              <p className="text-base lg:text-lg text-emerald-100/70 leading-relaxed">
                {t('home.landing.ai.body2')}
              </p>
            </div>

            <div className="pt-4 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('home.landing.ai.b1Title')}</div>
                  <div className="text-emerald-100/60 text-sm">{t('home.landing.ai.b1Body')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('home.landing.ai.b2Title')}</div>
                  <div className="text-emerald-100/60 text-sm">{t('home.landing.ai.b2Body')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('home.landing.ai.b3Title')}</div>
                  <div className="text-emerald-100/60 text-sm">{t('home.landing.ai.b3Body')}</div>
                </div>
              </div>
            </div>

            <div className="pt-6 px-6 py-4 rounded-2xl bg-emerald-950/50 border border-emerald-500/30">
              <p className="text-sm text-emerald-100/70 leading-relaxed">
                <span className="font-semibold text-emerald-300">{t('home.landing.ai.importantLead')}</span> {t('home.landing.ai.importantBody')}
              </p>
            </div>
          </div>

          {/* Right Side - AI Preview Cards */}
          <div className="lg:col-span-7 space-y-4">
            {/* Main AI Insight Card */}
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500/5 blur-2xl rounded-3xl"></div>
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl p-6 lg:p-8 border border-blue-500/30 shadow-2xl overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>

                <div className="relative space-y-5">
                  {/* Header */}
                  <div className="flex items-start justify-between pb-4 border-b border-emerald-500/30">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span className="text-sm text-blue-300/80 font-semibold">{t('home.landing.ai.cardAssistant')}</span>
                      </div>
                      <div className="text-xl font-bold text-white">{t('home.landing.ai.cardCycleTitle')}</div>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-400/30 text-xs font-semibold text-blue-300">
                      {t('home.landing.ai.insight')}
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-950/60 border border-emerald-500/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm font-semibold text-emerald-300 mb-1">{t('home.landing.ai.i1Title')}</div>
                        <div className="text-xs text-emerald-100/60">{t('home.landing.ai.i1Body')}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-950/40 border border-amber-500/30">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm font-semibold text-amber-300 mb-1">{t('home.landing.ai.i2Title')}</div>
                        <div className="text-xs text-amber-100/60">{t('home.landing.ai.i2Body')}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-950/40 border border-blue-500/30">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm font-semibold text-blue-300 mb-1">{t('home.landing.ai.i3Title')}</div>
                        <div className="text-xs text-blue-100/60">{t('home.landing.ai.i3Body')}</div>
                      </div>
                    </div>
                  </div>

                  {/* Confirmation Required */}
                  <div className="pt-4 border-t border-emerald-500/30">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-emerald-100/60">{t('home.landing.ai.confirm')}</div>
                      <button type="button" className="px-4 py-2 rounded-lg bg-emerald-600/20 text-emerald-300 text-sm font-semibold border border-emerald-500/30 hover:bg-emerald-600/30 transition-all">
                        {t('home.landing.ai.more')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Three Small Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-purple-500/30 shadow-xl overflow-hidden group hover:border-purple-400/40 transition-all">
                <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full blur-2xl"></div>
                <div className="relative space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-emerald-500/30 border border-purple-400/40 flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white mb-1">{t('home.landing.ai.s1Title')}</div>
                    <div className="text-xs text-emerald-100/60 leading-relaxed">{t('home.landing.ai.s1Body')}</div>
                  </div>
                </div>
              </div>

              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-amber-500/30 shadow-xl overflow-hidden group hover:border-amber-400/40 transition-all">
                <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full blur-2xl"></div>
                <div className="relative space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 to-emerald-500/30 border border-amber-400/40 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white mb-1">{t('home.landing.ai.s2Title')}</div>
                    <div className="text-xs text-emerald-100/60 leading-relaxed">{t('home.landing.ai.s2Body')}</div>
                  </div>
                </div>
              </div>

              <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-blue-500/30 shadow-xl overflow-hidden group hover:border-blue-400/40 transition-all">
                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl"></div>
                <div className="relative space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 border border-blue-400/40 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white mb-1">{t('home.landing.ai.s3Title')}</div>
                    <div className="text-xs text-emerald-100/60 leading-relaxed">{t('home.landing.ai.s3Body')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative z-10 sg20-landing-shell py-16 sm:py-24 lg:py-32">
        <div className="relative">
          <div className="absolute inset-0 bg-emerald-500/5 blur-[100px] rounded-full"></div>
          <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-2xl rounded-3xl lg:rounded-[48px] p-6 sm:p-8 lg:p-12 border border-emerald-500/30 shadow-2xl overflow-hidden">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-emerald-400/8 rounded-full blur-[80px]"></div>

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left Side - CTA Text */}
              <div className="lg:col-span-5 space-y-6 lg:space-y-8">
                <div className="space-y-4 lg:space-y-6">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                    {tr('home.public.ctaTitle', 'Готовы собрать выращивание в один рабочий слой?')}
                  </h2>
                  <p className="text-lg sm:text-xl text-emerald-100/70 leading-relaxed">
                    {tr('home.public.ctaBody', 'Shefa Green помогает команде видеть сорта, комнаты, устройства, климат, воду, дневник, задачи и аналитику в одном защищённом workspace.')}
                  </p>
                </div>

                <div>
                  <Link to={primaryCtaTo} className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-bold text-lg sm:text-xl transition-all hover:shadow-2xl hover:shadow-emerald-500/60 hover:scale-[1.02] shadow-2xl shadow-emerald-600/40 inline-flex items-center justify-center no-underline">
                {tr('home.hero.openDashboard', 'Открыть дашборд')}
              </Link>
                </div>

                {/* Status Chips */}
                <div className="flex flex-wrap gap-2 sm:gap-3 pt-4">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-950/70 border border-emerald-500/30">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/60"></div>
                    <span className="text-sm text-emerald-300">{t('home.landing.cta.chipClimate')}</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-950/70 border border-emerald-500/30">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/60"></div>
                    <span className="text-sm text-emerald-300">{t('home.landing.cta.chipOutlets')}</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-950/70 border border-emerald-500/30">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/60"></div>
                    <span className="text-sm text-emerald-300">{t('home.landing.cta.chipSecure')}</span>
                  </div>
                </div>

                <div className="pt-4">
                  <div className="px-6 py-3 rounded-xl bg-emerald-950/50 border border-emerald-500/30">
                    <p className="text-xs text-emerald-100/60 leading-relaxed">
                      {tr('home.public.notice', 'Операционные данные доступны только после входа. Публичные посетители не видят фермы, комнаты, устройства, сенсорные значения, задачи, дневник или аналитику.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Side - Workspace Preview */}
              <div className="lg:col-span-7 relative">
                {/* Connection Lines */}
                <div className="hidden sm:block absolute top-1/2 left-8 right-8 h-0.5 bg-gradient-to-r from-emerald-500/20 via-emerald-500/30 to-emerald-500/20 -translate-y-1/2"></div>
                <div className="hidden sm:block absolute top-1/4 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent rotate-45 origin-center"></div>

                <div className="grid grid-cols-2 gap-3 sm:gap-4 relative z-10">
                  {/* Сорт */}
                  <div className="relative group">
                    <div className="absolute inset-0 bg-purple-500/5 blur-xl rounded-2xl group-hover:bg-purple-500/10 transition-all"></div>
                    <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-purple-500/30 shadow-xl hover:border-purple-400/40 transition-all">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-purple-500/10 rounded-full blur-xl"></div>
                      <div className="relative space-y-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-emerald-500/30 border border-purple-400/40 flex items-center justify-center">
                          <div className="w-4 h-4 rounded-full bg-purple-400/80"></div>
                        </div>
                        <div>
                          <div className="text-xs text-purple-400/70 mb-1">{t('home.landing.mini.strain')}</div>
                          <div className="text-lg font-bold text-white">{t('home.landing.demo.strainName')}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Комната */}
                  <div className="relative group">
                    <div className="absolute inset-0 bg-cyan-500/5 blur-xl rounded-2xl group-hover:bg-cyan-500/10 transition-all"></div>
                    <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/30 shadow-xl hover:border-cyan-400/40 transition-all">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-full blur-xl"></div>
                      <div className="relative space-y-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 border border-cyan-400/40 flex items-center justify-center">
                          <svg className="w-5 h-5 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs text-cyan-400/70 mb-1">{t('home.landing.mini.room')}</div>
                          <div className="text-lg font-bold text-white">A1</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Контроллер */}
                  <div className="relative group">
                    <div className="absolute inset-0 bg-emerald-500/5 blur-xl rounded-2xl group-hover:bg-emerald-500/10 transition-all"></div>
                    <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-emerald-500/30 shadow-xl hover:border-emerald-400/40 transition-all">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full blur-xl"></div>
                      <div className="relative space-y-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-emerald-400/40 flex items-center justify-center">
                          <svg className="w-5 h-5 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs text-emerald-400/70 mb-1">{t('home.landing.mini.controller')}</div>
                          <div className="text-lg font-bold text-white">{t('deviceCard.connectivityOnline')}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Дневник */}
                  <div className="relative group">
                    <div className="absolute inset-0 bg-amber-500/5 blur-xl rounded-2xl group-hover:bg-amber-500/10 transition-all"></div>
                    <div className="relative bg-gradient-to-br from-emerald-950/80 to-emerald-900/50 backdrop-blur-xl rounded-2xl p-5 border border-amber-500/30 shadow-xl hover:border-amber-400/40 transition-all">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/10 rounded-full blur-xl"></div>
                      <div className="relative space-y-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 to-emerald-500/30 border border-amber-400/40 flex items-center justify-center">
                          <svg className="w-5 h-5 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs text-amber-400/70 mb-1">{t('home.landing.mini.diary')}</div>
                          <div className="text-lg font-bold text-white">{t('home.landing.mini.diaryEntries', 42)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-emerald-500/20 backdrop-blur-xl bg-[#0f3329]/90 shadow-inner shadow-emerald-950/20">
        <div className="sg20-landing-shell py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
            <div className="text-sm text-emerald-100/50 text-center sm:text-left">
              {t('home.landing.footer.copyright')}
            </div>
            <div className="flex items-center gap-4 sm:gap-8">
              <a href="#" className="text-sm text-emerald-100/60 hover:text-emerald-100 transition-colors">
                {t('home.landing.footer.telegram')}
              </a>
              <div className="text-sm text-emerald-100/50">
                {t('language')}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
